import React, { type HTMLAttributes, type PropsWithChildren, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import { ReadLess, ReadMore } from 'autogen/translation-keys/trans-website-shared-buttons';
import InlineButton from 'components/generic/InlineButton';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';

const Container = styled.div``;

const Text = styled.div<{ $lines: ReadMoreLineClampProps['linesToShow']; $clamp: boolean }>`
    display: -webkit-box;
    -webkit-line-clamp: ${({ $clamp, $lines }) => ($clamp ? $lines : 'unset')};
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const StyledButton = styled(InlineButton)`
    padding: ${Spacing8} 0;
    height: auto;
`;

type ReadMoreLineClampProps = PropsWithChildren & {
    linesToShow: number;
    ButtonRenderer?: (props: HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>) => ReactNode;
};

function getNumberOfTextLines(element: Element) {
    const { lineHeight, height } = getComputedStyle(element);

    return Number.parseInt(height) / Number.parseInt(lineHeight);
}

export default function ReadMoreLineClamp(props: ReadMoreLineClampProps) {
    const { children, linesToShow, ButtonRenderer = (props) => <StyledButton {...props} />, ...restProps } = props;
    const { t } = useTranslation();
    const textRef = useRef<HTMLDivElement>(null);
    const [clamp, setClamp] = useState(true);
    const [visibleLines, setVisibleLines] = useState(linesToShow);
    const [showButtons, setShowButtons] = useState(true);
    const GA4Tracking = useGA4Tracking();

    function onClickHandler() {
        setClamp((prev) => !prev);

        GA4Tracking({
            eventName: 'read_more'
        });
    }

    useEffect(() => {
        const textNode = textRef.current;

        if (textNode) {
            const mutationCallback: ResizeObserverCallback = (entries: ResizeObserverEntry[]) => {
                for (const entry of entries) {
                    const { target } = entry;
                    const { clientHeight, scrollHeight } = target;

                    setClamp(scrollHeight > clientHeight);
                    setVisibleLines(getNumberOfTextLines(target));
                }
            };

            const observer = new ResizeObserver(mutationCallback);

            observer.observe(textNode);
        }
    }, []);

    useEffect(() => {
        setShowButtons(clamp || visibleLines > linesToShow);
    }, [clamp, visibleLines, linesToShow]);

    return (
        <Container {...restProps}>
            <Text $lines={linesToShow} $clamp={clamp} ref={textRef}>
                {children}
            </Text>
            {showButtons && (
                <ButtonRenderer onClick={onClickHandler}>{clamp ? t(ReadMore) : t(ReadLess)}</ButtonRenderer>
            )}
        </Container>
    );
}
